/**
 * Density-aware subsample for scatter DOM rendering: one reservoir sample per 2D bin
 * in data space (linear domain). Sparse regions keep distinct points; dense stacks collapse.
 * Pure O(n) over data; bounded Map size ≤ bx×by.
 *
 * Grid uses many more cells than the final DOM cap (`GRID_CELLS_PER_RENDER_CAP`) so bin
 * boundaries are much subtler; candidates are then randomly capped to maxPoints (selected
 * rows are always kept).
 */

export interface ScatterRenderPoint {
    X: number;
    Y: number;
    ID?: number | null;
    [key: string]: unknown;
}

function binIndex(v: number, vMin: number, vMax: number, bins: number): number {
    if (bins <= 1) return 0;
    if (!Number.isFinite(v) || !Number.isFinite(vMin) || !Number.isFinite(vMax)) return 0;
    const lo = Math.min(vMin, vMax);
    const hi = Math.max(vMin, vMax);
    if (hi === lo) return 0;
    const t = (v - lo) / (hi - lo);
    const clamped = Math.min(1, Math.max(0, t));
    return Math.min(bins - 1, Math.floor(clamped * bins));
}

type BinReservoir = { candidate: ScatterRenderPoint; count: number };

/** Bin count ≈ maxPoints × this factor before capping to maxPoints (finer = less visible lattice). */
export const GRID_CELLS_PER_RENDER_CAP = 10;

function pickGridBinsForCellCount(targetCellCount: number): { bx: number; by: number } {
    const n = Math.max(1, Math.ceil(targetCellCount));
    const bx = Math.max(1, Math.round(Math.sqrt(n)));
    const by = Math.max(1, Math.ceil(n / bx));
    return { bx, by };
}

/**
 * @param data Full reduced scatter rows (same source as fits / filtered()).
 * @param maxPoints Hard cap on returned rows after binning + selected union + trim.
 * @param xDomain d3 linear scale domain (may be reversed).
 * @param yDomain d3 linear scale domain (may be reversed).
 * @param selectedIds Event IDs that must appear in the output if present in data.
 */
export function sampleScatterForRender(
    data: ScatterRenderPoint[],
    maxPoints: number,
    xDomain: readonly [number, number],
    yDomain: readonly [number, number],
    selectedIds: readonly number[]
): ScatterRenderPoint[] {
    if (data.length <= maxPoints || maxPoints < 1) {
        return data.slice();
    }

    const [x0, x1] = xDomain;
    const [y0, y1] = yDomain;
    const gridCells = Math.max(64, Math.ceil(maxPoints * GRID_CELLS_PER_RENDER_CAP));
    const { bx, by } = pickGridBinsForCellCount(gridCells);
    const bins = new Map<number, BinReservoir>();

    for (const d of data) {
        const x = Number(d.X);
        const y = Number(d.Y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;

        const ix = binIndex(x, x0, x1, bx);
        const iy = binIndex(y, y0, y1, by);
        const key = ix * by + iy;

        const entry = bins.get(key);
        if (!entry) {
            bins.set(key, { candidate: d, count: 1 });
        } else {
            entry.count += 1;
            if (Math.random() < 1 / entry.count) {
                entry.candidate = d;
            }
        }
    }

    const fromBins: ScatterRenderPoint[] = [];
    for (const { candidate } of bins.values()) {
        fromBins.push(candidate);
    }

    const selectedSet = new Set(selectedIds.filter((id) => id != null && Number.isFinite(id)));

    const byId = new Map<number, ScatterRenderPoint>();
    const noId: ScatterRenderPoint[] = [];
    for (const d of fromBins) {
        const id = d.ID;
        if (id !== undefined && id !== null && Number.isFinite(Number(id))) {
            byId.set(Number(id), d);
        } else {
            noId.push(d);
        }
    }

    if (selectedSet.size > 0) {
        for (const d of data) {
            const id = d.ID;
            if (id === undefined || id === null || !Number.isFinite(Number(id))) continue;
            const nid = Number(id);
            if (selectedSet.has(nid)) {
                byId.set(nid, d);
            }
        }
    }

    let combined: ScatterRenderPoint[] = [...noId, ...byId.values()];

    if (combined.length <= maxPoints) {
        return combined;
    }

    const selectedRows: ScatterRenderPoint[] = [];
    const otherRows: ScatterRenderPoint[] = [];
    for (const d of combined) {
        const id = d.ID;
        if (id !== undefined && id !== null && selectedSet.has(Number(id))) {
            selectedRows.push(d);
        } else {
            otherRows.push(d);
        }
    }

    const capOthers = Math.max(0, maxPoints - selectedRows.length);
    if (otherRows.length > capOthers) {
        for (let i = 0; i < capOthers; i++) {
            const j = i + Math.floor(Math.random() * (otherRows.length - i));
            const t = otherRows[i];
            otherRows[i] = otherRows[j];
            otherRows[j] = t;
        }
        otherRows.length = capOthers;
    }

    return [...selectedRows, ...otherRows];
}

/** Row shape used by performance timeline strips (time on x, BSP/TWS on y). */
export interface TimelineRenderPoint {
    datetime: Date;
    event_id?: unknown;
}

function parseTimelineEventId(raw: unknown): number | undefined {
    if (raw === undefined || raw === null || raw === '') return undefined;
    const id = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
    return Number.isFinite(id) ? id : undefined;
}

/**
 * Density-aware subsample for ScatterTimeseries DOM rendering.
 * Brush/selection should use the full `data` array; this caps SVG circle count only.
 */
export function sampleTimelinePointsForRender<T extends TimelineRenderPoint>(
    data: T[],
    maxPoints: number,
    yValue: (row: T) => number,
    selectedIds: readonly number[]
): T[] {
    if (data.length <= maxPoints || maxPoints < 1) {
        return data.slice();
    }

    const timeMs = data.map((row) => row.datetime.getTime()).filter(Number.isFinite);
    const yVals = data.map((row) => yValue(row)).filter(Number.isFinite);
    if (timeMs.length === 0 || yVals.length === 0) {
        return data.slice(0, maxPoints);
    }

    const xDom: [number, number] = [Math.min(...timeMs), Math.max(...timeMs)];
    const yDom: [number, number] = [Math.min(...yVals), Math.max(...yVals)];

    const rowById = new Map<number, T>();
    const scatterRows: ScatterRenderPoint[] = data.map((row) => {
        const id = parseTimelineEventId(row.event_id);
        if (id !== undefined) {
            rowById.set(id, row);
        }
        return {
            X: row.datetime.getTime(),
            Y: yValue(row),
            ID: id,
        };
    });

    const sampled = sampleScatterForRender(scatterRows, maxPoints, xDom, yDom, selectedIds);
    const out: T[] = [];
    const seen = new Set<T>();
    for (const point of sampled) {
        const id = point.ID;
        if (id !== undefined && id !== null && rowById.has(id)) {
            const row = rowById.get(id)!;
            if (!seen.has(row)) {
                seen.add(row);
                out.push(row);
            }
            continue;
        }
        const match = data.find(
            (row) =>
                row.datetime.getTime() === point.X &&
                Math.abs(yValue(row) - point.Y) < 1e-9
        );
        if (match && !seen.has(match)) {
            seen.add(match);
            out.push(match);
        }
    }
    return out.length > 0 ? out : data.slice(0, maxPoints);
}
